import { useEffect, useRef } from 'react';
import axios from 'axios';
import {
  isSoundEnabled,
  hasSeen,
  markSeen,
  playChime,
} from '@/lib/notificationSound';

const API_URL = process.env.REACT_APP_API_URL;
const POLL_MS = 25_000;

/** Polls /api/notifications and plays a chime for any new notification whose
 *  `metadata.source === 'public_booking'`. No-op when:
 *    - sound preference is disabled,
 *    - the document is hidden (battery friendly),
 *    - the notification was already chimed for (seen-set in localStorage).
 *
 *  Tenant isolation is implicit — the backend endpoint already scopes results
 *  to the caller's user_id, which belongs to a single business_id.
 */
export default function useNewBookingSound({ enabled = true } = {}) {
  const seededRef = useRef(false);

  useEffect(() => {
    if (!enabled) return undefined;

    let cancelled = false;
    let timer = null;

    const tick = async () => {
      if (cancelled) return;
      if (document.hidden) return; // skip while tab is hidden
      if (!isSoundEnabled()) return; // user hasn't opted in

      try {
        const { data } = await axios.get(`${API_URL}/api/notifications`, {
          withCredentials: true,
        });
        const bookings = (Array.isArray(data) ? data : []).filter(
          (n) => n?.metadata?.source === 'public_booking',
        );

        // On the very first poll, just record what's there so we don't chime
        // for everything historical. After that, any unseen entry → chime.
        if (!seededRef.current) {
          markSeen(bookings.map((n) => n.id));
          seededRef.current = true;
          return;
        }

        const unseen = bookings.filter((n) => !hasSeen(n.id));
        if (unseen.length > 0) {
          playChime();
          markSeen(unseen.map((n) => n.id));
        }
      } catch {
        /* network errors are fine — try again next tick */
      }
    };

    // Run immediately to seed the seen-set, then on an interval.
    tick();
    timer = setInterval(tick, POLL_MS);

    // Refresh on tab-focus, but debounced so rapid tab switches don't hammer the API.
    let focusTimer = null;
    const onVisibility = () => {
      if (document.hidden) return;
      if (focusTimer) clearTimeout(focusTimer);
      focusTimer = setTimeout(tick, 1500);
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
      if (focusTimer) clearTimeout(focusTimer);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [enabled]);
}
